import { useMemo } from 'react';
import { Box } from '@mui/material';
import { ValueFormatterFunc, ValueParserFunc } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import TableGridItem from '../UI/TableGridItem';
import { BillingConfigWithRelations } from '../../services/budgetBillingConfig';
import { PendingConfigEdits } from '../../hooks/usePendingConfigEdits';
import { priceFormatter, parseBudgetCell } from './helpers';
import { useRateGrid } from './useRateGrid';

interface MinutePriceGridProps {
    billingConfigs: BillingConfigWithRelations[];
    isLoading: boolean;
    /** The page's pending edits — this grid records into them; the page saves them (CALL-779). */
    pending: PendingConfigEdits;
    /** CALL-781: hide month COLUMNS before the current month. Display only. */
    hidePastMonths?: boolean;
}

const formatPrice: ValueFormatterFunc = (params) => {
    if (params.value == null || params.value === '') return '';
    return priceFormatter.format(Number(params.value));
};

// The shared budget cell parser: strips the unit the formatter added and reads the rest as German, so a
// price pasted from Excel as "1.234,56" is 1234.56 on any browser (CALL-794). It answers null for an
// emptied cell, which the batch save turns into "delete this month".
const parsePrice: ValueParserFunc = (params) => parseBudgetCell(params.newValue);

const getEnteredPrices = (config: BillingConfigWithRelations): Map<string, number> =>
    new Map((config.minutePrices || []).map((mp) => [String(mp.month), Number(mp.price)]));

// CALL-824: an entered monthly price overrides the Standard-Minutenpreis in the cost calculation, so a
// diverging cell is tinted and names the overridden value — the CALL-822 cue, applied to prices.
const getDefaultMinutePrice = (config: BillingConfigWithRelations): number | null =>
    config.defaultMinutePrice == null ? null : Number(config.defaultMinutePrice);

// CALL-745: minute prices are global master data; editable for budget users (not gated by the
// selected version's snapshot state). CALL-752: empty future cells are forward-extrapolated (blue).
const MinutePriceGrid = ({ billingConfigs, isLoading, pending, hidePastMonths = false }: MinutePriceGridProps) => {
    const { t } = useTranslation('budget');
    const divergence = useMemo(
        () => ({
            getReference: getDefaultMinutePrice,
            getTooltip: (reference: number) =>
                t('prices.overridesDefaultMinutePrice', { value: priceFormatter.format(reference) }),
        }),
        [t]
    );
    const { gridRef, rowData, columnDefs, defaultColDef, onCellValueChanged, onCellEditingStopped } = useRateGrid({
        hidePastMonths,
        billingConfigs,
        editKind: 'minutePriceEdits',
        pending,
        minWidth: 100,
        valueFormatter: formatPrice,
        valueParser: parsePrice,
        getEnteredMap: getEnteredPrices,
        divergence,
    });

    // CALL-794: the `budget-grid` container the other editable budget grids carry. It is not decoration:
    // App.css sizes an inline editor to its row through `body .budget-grid .ag-cell-inline-editing`, and
    // without an ancestor carrying the class the editor opens at the material theme's 72px, overlapping the
    // row below. A plain div, like WorkingUnitMappingGrid's — nothing here needs MUI's Box.
    return (
        <Box className="budget-grid">
            <TableGridItem
                ref={gridRef}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                onCellValueChanged={onCellValueChanged}
                onCellEditingStopped={onCellEditingStopped}
                getRowId={(params) => String(params.data.id)}
                loading={isLoading}
                cellSelection
                height={'auto'}
                noRowsOverlayComponent={() => <span style={{ padding: 10 }}>{t('hint.noMinutePrices')}</span>}
            />
        </Box>
    );
};

export default MinutePriceGrid;
